import { createContext, useContext, useState, useEffect } from 'react';
import { endpoints } from '../components/Chat/Endpoints.jsx';
import { UserContext } from '../components/Auth.jsx';

// Create the context
const LLMSettingsContext = createContext();

// Default values
const DEFAULT_LLM = '/api/toolchat/gpt-4.1';
const DEFAULT_SYSTEM_PROMPT = '';
const DEFAULT_USER_PROMPT = '';
const DEFAULT_HISTORY_LIMIT = 5;

// Helper function to validate if an LLM value exists in current endpoints
const isValidLLM = (llmValue) => {
  if (!llmValue) return false;
  return endpoints.some(endpoint => endpoint.value === llmValue);
};

// Helper function to get a safe LLM value
const getSafeLLMValue = (savedValue) => {
  // If no saved value, use default
  if (!savedValue) {
    console.log('LLM Settings: No saved value found, using default:', DEFAULT_LLM);
    return DEFAULT_LLM;
  }
  
  // If saved value is valid in current endpoints, use it
  if (isValidLLM(savedValue)) {
    console.log('LLM Settings: Using saved value:', savedValue);
    return savedValue;
  }
  
  // If saved value is invalid (e.g., from old LLM options), use default
  console.log('LLM Settings: Saved value invalid, falling back to default. Saved:', savedValue, 'Default:', DEFAULT_LLM);
  return DEFAULT_LLM;
};

// Provider component
export const LLMSettingsProvider = ({ children }) => {
  // Access user context to determine admin status (may be undefined during initial load)
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  
  // LLM Settings
  const [selectedLLM, setSelectedLLM] = useState(() => {
    const saved = localStorage.getItem('globalLLMSetting');
    return getSafeLLMValue(saved);
  });

  // Chat Settings
  const [systemPrompt, setSystemPrompt] = useState(() => {
    const saved = localStorage.getItem('globalSystemPrompt');
    return saved || DEFAULT_SYSTEM_PROMPT;
  });

  const [userPrompt, setUserPrompt] = useState(() => {
    const saved = localStorage.getItem('globalUserPrompt');
    return saved || DEFAULT_USER_PROMPT;
  });

  const [historyLimit, setHistoryLimit] = useState(() => {
    const saved = localStorage.getItem('globalHistoryLimit');
    return saved ? parseInt(saved, 10) : DEFAULT_HISTORY_LIMIT;
  });

  // Universal prompt states
  const [universalPrompts, setUniversalPrompts] = useState([]);
  const [loadedUniversalPromptId, setLoadedUniversalPromptId] = useState(() => {
    const saved = localStorage.getItem('loadedUniversalPromptId');
    return saved || null;
  });
  const [universalPromptsLoading, setUniversalPromptsLoading] = useState(false);

  // Clean up localStorage on initialization if we had to fallback to default
  useEffect(() => {
    const saved = localStorage.getItem('globalLLMSetting');
    if (saved && !isValidLLM(saved)) {
      console.log('LLM Settings: Cleaning up invalid localStorage value:', saved);
      localStorage.setItem('globalLLMSetting', selectedLLM);
    }
  }, []); // Run only once on mount

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem('globalLLMSetting', selectedLLM);
  }, [selectedLLM]);

  useEffect(() => {
    localStorage.setItem('globalSystemPrompt', systemPrompt);
  }, [systemPrompt]);

  useEffect(() => {
    localStorage.setItem('globalUserPrompt', userPrompt);
  }, [userPrompt]);

  useEffect(() => {
    localStorage.setItem('globalHistoryLimit', historyLimit.toString());
  }, [historyLimit]);

  useEffect(() => {
    if (loadedUniversalPromptId) {
      localStorage.setItem('loadedUniversalPromptId', loadedUniversalPromptId);
    } else {
      localStorage.removeItem('loadedUniversalPromptId');
    }
  }, [loadedUniversalPromptId]);

  // Update functions
  const updateLLM = (newLLM) => {
    // Validate the new LLM value before setting it
    const safeLLM = getSafeLLMValue(newLLM);
    setSelectedLLM(safeLLM);
    
    // If the safe value is different from what was requested, log it
    if (safeLLM !== newLLM) {
      console.warn('LLM Settings: Requested LLM value was invalid, using safe fallback:', { requested: newLLM, used: safeLLM });
    }
  };

  const updateSystemPrompt = (newPrompt) => {
    setSystemPrompt(newPrompt);
    // Clear the loaded universal prompt ID when manually changing the prompt
    setLoadedUniversalPromptId(null);
  };

  const updateUserPrompt = (newPrompt) => {
    setUserPrompt(newPrompt);
  };

  const updateHistoryLimit = (newLimit) => {
    setHistoryLimit(newLimit);
  };

  // Universal prompt functions
  const loadUniversalPrompts = async () => {
    try {
      setUniversalPromptsLoading(true);
      const response = await fetch('/api/prompts', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const prompts = await response.json();
        // Filter to only universal prompts
        const universalPrompts = prompts.filter(p => 
          p.metadata && JSON.parse(p.metadata).type === 'universal'
        );
        setUniversalPrompts(universalPrompts);
        
        // Auto-load universal prompt logic (only if user context is available)
        if (universalPrompts.length > 0 && user !== undefined) {
          const universalPrompt = universalPrompts[0]; // Assume only one universal prompt
          
          if (!user?.admin) {
            // For non-admin users: always use the universal prompt (they can't access settings to change it)
            setSystemPrompt(universalPrompt.content);
            setLoadedUniversalPromptId(universalPrompt.id.toString());
          } else {
            // For admin users: load universal prompt as default only if they haven't customized anything yet
            const hasExistingSelection = localStorage.getItem('loadedUniversalPromptId');
            const hasCustomPrompt = localStorage.getItem('globalSystemPrompt') && 
                                   localStorage.getItem('globalSystemPrompt') !== DEFAULT_SYSTEM_PROMPT;
            
            if (!hasExistingSelection && !hasCustomPrompt) {
              setSystemPrompt(universalPrompt.content);
              setLoadedUniversalPromptId(universalPrompt.id.toString());
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to load universal prompts:', err);
    } finally {
      setUniversalPromptsLoading(false);
    }
  };

  const loadUniversalPrompt = (promptId) => {
    const prompt = universalPrompts.find(p => p.id === promptId);
    if (prompt) {
      setSystemPrompt(prompt.content);
      setLoadedUniversalPromptId(promptId);
    }
  };

  const clearUniversalPrompt = () => {
    setLoadedUniversalPromptId(null);
    setSystemPrompt(DEFAULT_SYSTEM_PROMPT);
  };

  const resetToUniversalPrompt = () => {
    if (loadedUniversalPromptId) {
      const prompt = universalPrompts.find(p => p.id === parseInt(loadedUniversalPromptId));
      if (prompt) {
        setSystemPrompt(prompt.content);
      }
    }
  };

  // Load universal prompts on mount
  useEffect(() => {
    loadUniversalPrompts();
  }, []);

  // Re-run auto-loading logic when user context becomes available
  useEffect(() => {
    if (user !== undefined && universalPrompts.length > 0) {
      const universalPrompt = universalPrompts[0];
      
      if (!user?.admin) {
        // For non-admin users: always use the universal prompt (they can't access settings to change it)
        setSystemPrompt(universalPrompt.content);
        setLoadedUniversalPromptId(universalPrompt.id.toString());
      } else {
        // For admin users: load universal prompt as default only if they haven't customized anything yet
        const hasExistingSelection = localStorage.getItem('loadedUniversalPromptId');
        const hasCustomPrompt = localStorage.getItem('globalSystemPrompt') && 
                               localStorage.getItem('globalSystemPrompt') !== DEFAULT_SYSTEM_PROMPT;
        
        if (!hasExistingSelection && !hasCustomPrompt) {
          setSystemPrompt(universalPrompt.content);
          setLoadedUniversalPromptId(universalPrompt.id.toString());
        }
      }
    }
  }, [user, universalPrompts]);

  const value = {
    // LLM Settings
    selectedLLM,
    updateLLM,
    // Chat Settings
    systemPrompt,
    updateSystemPrompt,
    userPrompt,
    updateUserPrompt,
    historyLimit,
    updateHistoryLimit,
    // Universal Prompts
    universalPrompts,
    loadedUniversalPromptId,
    universalPromptsLoading,
    loadUniversalPrompts,
    loadUniversalPrompt,
    clearUniversalPrompt,
    resetToUniversalPrompt,
  };

  return (
    <LLMSettingsContext.Provider value={value}>
      {children}
    </LLMSettingsContext.Provider>
  );
};

// Custom hook to use the LLM settings context
export const useLLMSettings = () => {
  const context = useContext(LLMSettingsContext);
  if (!context) {
    throw new Error('useLLMSettings must be used within a LLMSettingsProvider');
  }
  return context;
};

export default LLMSettingsContext;
